import { createDeserializer } from 'minecraft-protocol/src/transforms/serializer.js'
import States from 'minecraft-protocol'

const deserializerCache = new Map<string, ReturnType<typeof createDeserializer>>()

export interface PacketsFileHeader {
  formatVersion: number
  minecraftVersion: string
  replayAgainst?: 'client' | 'server'
  serverIp?: string
}

export interface ParsedReplayPacket {
  name: string
  params: any
  state: string
  isFromServer: boolean
  diff?: number
  time?: number
}

/**
 * Parses replay file contents and returns header + packets.
 * Supports legacy JSON format, hex bytebuf format, and base64 bytebuf format.
 */
export async function parseReplayContents(contents: string): Promise<{
  packets: ParsedReplayPacket[]
  header: PacketsFileHeader
}> {
  const lines = contents.split('\n').filter(line => line.trim())

  if (lines.length === 0) {
    throw new Error('Replay file is empty')
  }

  // Parse header (first line is always JSON)
  let header: PacketsFileHeader
  try {
    header = JSON.parse(lines[0]!)
  } catch (err) {
    throw new Error(`Invalid JSON in file header: ${String(err)}`)
  }

  const packets: ParsedReplayPacket[] = []
  const version = header.minecraftVersion

  // Aggregate parse errors instead of logging per-packet (replays may have thousands
  // of corrupted packets, e.g. when binary bytes were UTF-8-mangled into 0xefbfbd
  // by the recorder, and per-packet console.warn would freeze the browser DevTools).
  const parseErrorCounts = new Map<string, number>()
  const unknownStateCounts = new Map<string, number>()
  let malformedPacketCount = 0
  const bumpError = (key: string) => parseErrorCounts.set(key, (parseErrorCounts.get(key) ?? 0) + 1)

  // State mapping for minecraft-protocol
  const stateMap = {
    handshaking: States.states.HANDSHAKING,
    status: States.states.STATUS,
    login: States.states.LOGIN,
    configuration: States.states.CONFIGURATION,
    play: States.states.PLAY
  } as const

  // Yield after heavy split/filter so the loading screen can render
  await new Promise(resolve => setTimeout(resolve, 0))

  let lastYield = performance.now()

  // Parse packet lines (skip header)
  for (let i = 1; i < lines.length; i++) {
    // Time-based yield every ~16 ms so the tab doesn't freeze on huge replays
    if (i % 200 === 0 && performance.now() - lastYield > 16) {
      lastYield = performance.now()
      await new Promise(resolve => setTimeout(resolve, 0))
    }

    const line = lines[i]!.trim()
    if (!line || line.startsWith('#')) continue

    const [side, nameState, diff, ...data] = line.split(' ')
    if (!side || !nameState) continue

    const [state, name] = nameState.split(':')
    if (!state || !name) continue

    // Skip bundle delimiters and keep-alives
    if (name === 'bundle_delimiter' || name === 'keep_alive') continue

    const dataStr = data.join(' ')
    let params: any

    // Auto-detect format: JSON starts with '{', hex uses space-separated bytes (XX XX XX),
    // base64 is everything else binary (no spaces, not JSON)
    const isJsonFormat = dataStr && (dataStr.startsWith('{') || dataStr.startsWith('['))
    const isHexFormat = dataStr && !isJsonFormat && dataStr.includes(' ') &&
      /^[0-9a-fA-F]{2}( [0-9a-fA-F]{2})*$/.test(dataStr.trim())
    const isBase64Format = dataStr && !isJsonFormat && !isHexFormat &&
      dataStr !== 'undefined' && dataStr !== 'null' &&
      /^[A-Za-z0-9+/]+=*$/.test(dataStr.trim())

    if (isHexFormat || isBase64Format) {
      // Binary bytebuf format - deserialize from hex or base64
      // isServer: side === 'C' because:
      //   S packets (server→client) need 'toClient' direction → isServer: false
      //   C packets (client→server) need 'toServer' direction → isServer: true
      try {
        const mcState = stateMap[state as keyof typeof stateMap]
        if (!mcState) {
          unknownStateCounts.set(state, (unknownStateCounts.get(state) ?? 0) + 1)
          continue
        }

        const cacheKey = `${version}:${side.toUpperCase()}:${state}`
        let deserializer = deserializerCache.get(cacheKey)
        if (!deserializer) {
          deserializer = createDeserializer({
            isServer: side.toUpperCase() === 'C',
            state: mcState,
            version,
            customPackets: {}
          } as any)
          deserializerCache.set(cacheKey, deserializer)
        }

        const buffer = isBase64Format
          ? Buffer.from(dataStr.trim(), 'base64')
          : Buffer.from(dataStr.replace(/\s+/g, ''), 'hex')
        params = (deserializer.parsePacketBuffer(buffer) as any).data.params
      } catch {
        bumpError(`${isBase64Format ? 'base64' : 'hex'}:${name}`)
        params = {}
      }
    } else if (isJsonFormat || dataStr === 'undefined' || dataStr === 'null') {
      // Legacy JSON format
      try {
        params = dataStr === 'undefined' || dataStr === 'null' ? {} : JSON.parse(dataStr)
      } catch {
        bumpError(`json:${name}`)
        params = {}
      }
    } else {
      // Fallback for malformed data
      malformedPacketCount++
      params = {}
    }

    // Compute timing
    let computedDiff = 0
    let timestamp = 0

    if (diff && diff !== '+0') {
      const diffNum = Number(diff.slice(1))
      if (!Number.isNaN(diffNum)) {
        computedDiff = diffNum
        timestamp = (packets[packets.length - 1]?.time ?? 0) + computedDiff
      }
    } else if (packets.length > 0) {
      timestamp = packets[packets.length - 1]!.time ?? 0
    }

    packets.push({
      name,
      params,
      state,
      isFromServer: side.toUpperCase() === 'S',
      diff: computedDiff,
      time: timestamp
    })
  }

  // Single aggregated summary of parse failures (avoids spamming the console
  // with thousands of per-packet warnings, which would freeze DevTools).
  if (parseErrorCounts.size > 0 || unknownStateCounts.size > 0 || malformedPacketCount > 0) {
    const totalFailures = [...parseErrorCounts.values()].reduce((a, b) => a + b, 0)
      + [...unknownStateCounts.values()].reduce((a, b) => a + b, 0)
      + malformedPacketCount
    const summary: Record<string, number> = {}
    for (const [key, count] of parseErrorCounts.entries()) summary[key] = count
    for (const [key, count] of unknownStateCounts.entries()) summary[`unknown-state:${key}`] = count
    if (malformedPacketCount > 0) summary['malformed'] = malformedPacketCount
    console.warn(
      `[parseReplayContents] ${totalFailures} packets failed to parse out of ${packets.length + totalFailures}. ` +
      `These will be replayed with empty params (likely no visible effect for movement/animation packets, ` +
      `but world state may be incomplete). Breakdown:`,
      summary
    )
  }

  return { packets, header }
}
